import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Chip, ProgressBar } from "@heroui/react";
import { Award, Flame, Lock, Play, Star, Target, Trophy } from "lucide-react";
import { cn } from "@/lib/utils";
import { PageHeader } from "../kit";
import { GameSession } from "./GameSession";
import { POOLS, gameById } from "./registry";
import {
  BASE_POINTS, LEVEL_META, PASS_SCORE, PROGRESS_EVENT, SESSION_SIZE, STREAK_CAP, STREAK_STEP,
  dayStreak, isUnlocked, rankFor, readProgress, starsFor, type Progress,
} from "./session";
import { LEVELS, type Level } from "./types";

function useProgress(): Progress {
  const [p, setP] = useState(readProgress);
  useEffect(() => {
    const reload = () => setP(readProgress());
    window.addEventListener(PROGRESS_EVENT, reload);
    window.addEventListener("storage", reload);
    return () => {
      window.removeEventListener(PROGRESS_EVENT, reload);
      window.removeEventListener("storage", reload);
    };
  }, []);
  return p;
}

/**
 * Trading games: 10 quick questions at a time on real past charts and the
 * desk's own rules, from a pool of 20 per level. Scored on the decision,
 * never on what price did next.
 */
export function GamesPage() {
  const [params, setParams] = useSearchParams();
  const progress = useProgress();
  const asked = params.get("play");
  const playing = LEVELS.find(l => l === asked && isUnlocked(l, progress)) ?? null;
  const setPlaying = (level: Level | null) =>
    setParams(p => {
      const next = new URLSearchParams(p);
      if (level) next.set("play", level);
      else next.delete("play");
      return next;
    }, { replace: true });

  if (playing) return <GameSession key={playing} level={playing} pool={POOLS[playing]} onExit={() => setPlaying(null)} />;

  const rank = rankFor(progress.xp);
  const days = dayStreak(progress.days, Date.now());
  const answered = Object.values(progress.games).reduce((s, g) => s + g.played, 0);
  const weak = Object.entries(progress.games)
    .filter(([, g]) => g.played >= 2 && g.correct < g.played)
    .map(([id, g]) => ({ game: gameById(id), rate: g.correct / g.played, played: g.played }))
    .filter((w): w is typeof w & { game: NonNullable<typeof w.game> } => !!w.game)
    .sort((a, b) => a.rate - b.rate)
    .slice(0, 5);

  return (
    <div className="mx-auto w-full max-w-5xl space-y-5 pb-16">
      <PageHeader
        title="Trading games"
        description={`${SESSION_SIZE} quick questions on real past charts. Scored on your decision, never on luck.`}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat icon={<Award className="size-4" />} label="Rank" value={rank.title}>
          {rank.next ? (
            <>
              <ProgressBar aria-label={`XP to ${rank.next.title}`} value={rank.progress * 100} size="sm" color="accent" className="mt-1.5">
                <ProgressBar.Track><ProgressBar.Fill /></ProgressBar.Track>
              </ProgressBar>
              <p className="mt-1 text-2xs text-muted"><span className="num">{progress.xp}</span> / <span className="num">{rank.next.xp}</span> XP to {rank.next.title}</p>
            </>
          ) : <p className="mt-1 text-2xs text-muted"><span className="num">{progress.xp}</span> XP · top rank</p>}
        </Stat>
        <Stat icon={<Flame className="size-4" />} label="Days in a row" value={String(days)}>
          <p className="mt-1 text-2xs text-muted">{days > 0 ? "Play a session today to keep it going." : "Finish one session a day to start a streak."}</p>
        </Stat>
        <Stat icon={<Trophy className="size-4" />} label="Best run" value={`${progress.bestStreak} right in a row`} />
        <Stat icon={<Target className="size-4" />} label="Questions answered" value={String(answered)} />
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        {LEVELS.map((level, i) => (
          <LevelCard
            key={level}
            level={level}
            progress={progress}
            previous={i > 0 ? LEVELS[i - 1]! : null}
            onPlay={() => setPlaying(level)}
          />
        ))}
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <section className="rounded-2xl border border-border bg-surface p-4">
          <h2 className="text-sm font-semibold text-foreground">How it works</h2>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted">
            <li>Each level has 20 games. A session picks {SESSION_SIZE}, leaning toward the ones you miss most.</li>
            <li>Charts are real coins on a random past day. The date stays hidden until you answer, so you can't remember what came next.</li>
            <li>Right answer: +{BASE_POINTS} points, plus {STREAK_STEP} more for every right answer in a row (up to +{STREAK_CAP}).</li>
            <li>{PASS_SCORE} of {SESSION_SIZE} passes a level and unlocks the next one.</li>
            <li>After you answer, the faded candles show what price did next. That part is never scored: one trade's result is mostly luck, so the games grade the decision.</li>
          </ul>
        </section>
        <section className="rounded-2xl border border-border bg-surface p-4">
          <h2 className="text-sm font-semibold text-foreground">Your weak spots</h2>
          {weak.length === 0 ? (
            <p className="mt-2 text-sm text-muted">Play a few sessions: the games you miss most show up here, and sessions bring them back more often.</p>
          ) : (
            <ul className="mt-2 space-y-2">
              {weak.map(w => (
                <li key={w.game.id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="min-w-0">
                    <span className="block truncate text-foreground">{w.game.title}</span>
                    <span className="block truncate text-xs text-muted">{LEVEL_META[w.game.level].label} · {w.game.skill}</span>
                  </span>
                  <span className={cn("num shrink-0 text-xs", w.rate < 0.5 ? "text-loss" : "text-warning")}>{Math.round(w.rate * 100)}% of {w.played}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, children }: { icon: React.ReactNode; label: string; value: string; children?: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-3.5">
      <p className="flex items-center gap-1.5 text-2xs font-medium uppercase tracking-wide text-muted">{icon}{label}</p>
      <p className="mt-1 truncate text-base font-semibold text-foreground">{value}</p>
      {children}
    </div>
  );
}

function LevelCard({ level, progress, previous, onPlay }: { level: Level; progress: Progress; previous: Level | null; onPlay: () => void }) {
  const stat = progress.levels[level];
  const open = isUnlocked(level, progress);
  const stars = starsFor(stat.best);
  const count = POOLS[level].length;
  return (
    <section className={cn("flex flex-col rounded-2xl border bg-surface p-4", open ? "border-border" : "border-dashed border-border opacity-80")}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-foreground">{LEVEL_META[level].label}</h2>
        {stat.passed ? <Chip size="sm" color="success" variant="soft">Passed</Chip> : !open ? <Lock className="size-4 text-muted" aria-label="locked" /> : null}
      </div>
      <p className="mt-1 flex-1 text-sm text-muted">{LEVEL_META[level].blurb}</p>
      <div className="mt-3 flex items-center justify-between text-xs text-muted">
        <span><span className="num">{count}</span> games</span>
        <span className="flex items-center gap-1">
          {stat.sessions > 0 ? <>Best <span className="num text-foreground">{stat.best}/{SESSION_SIZE}</span></> : "Not played yet"}
          <span className="ml-1 flex" aria-label={`${stars} of 3 stars`}>
            {[1, 2, 3].map(i => <Star key={i} className={cn("size-3.5", i <= stars ? "fill-warning text-warning" : "text-default")} />)}
          </span>
        </span>
      </div>
      {open ? (
        <Button className="mt-3" variant={stat.sessions === 0 || !stat.passed ? "primary" : "secondary"} isDisabled={count === 0} onPress={onPlay}>
          <Play className="size-4" /> {stat.sessions === 0 ? "Start" : "Play 10"}
        </Button>
      ) : (
        <p className="mt-3 rounded-xl bg-surface-secondary px-3 py-2 text-xs text-muted">
          Pass {previous ? LEVEL_META[previous].label : "the level before"} ({PASS_SCORE} of {SESSION_SIZE} right) to unlock.
        </p>
      )}
    </section>
  );
}
